import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList } from '@angular/cdk/drag-drop';

import { Popup } from '../../../../../core/shared/components/popup/popup';
import { ToastService } from '../../../../../core/shared/services/toast.service';
import { CraditService } from '../../services/cradit.service';
import { RichTextEditor } from '../rich-text-editor/rich-text-editor';
import { SectionDragHandle } from '../section-drag-handle/section-drag-handle';
import { RichTextPipe } from '../../utils/rich-text.pipe';
import {
  hasRichTextContent,
  paragraphBlockData,
  readParagraphBlockText,
} from '../../utils/rich-text.util';
import { bySortOrder, moveVisibleItem } from '../../utils/section-reorder.util';

export interface ParagraphItem {
  content: string;
  expanded?: boolean;
  crdtPrpslBlkSeq?: number | null;
  delFlg?: number;
}

@Component({
  selector: 'app-paragraph-section',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Popup,
    TranslatePipe,
    RichTextEditor,
    RichTextPipe,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    SectionDragHandle,
  ],
  templateUrl: './paragraph-section.html',
  styleUrl: './paragraph-section.css',
})
export class ParagraphSection implements OnInit, OnChanges, OnDestroy {
  @Input() editable = false;
  @Input() refValSeq!: number;
  @Input() refGrp = 28;
  @Input() sectionId!: string;
  @Input() paragraphs: ParagraphItem[] = [];

  onboardingId!: number;

  crdtPrpslSeq: number | null = null;

  private saveSub?: Subscription;
  private seqSub?: Subscription;
  private initialized = false; // ✅ taake ngOnChanges pehli call par duplicate load na kare

  constructor(
    private creditService: CraditService,
    private route: ActivatedRoute,
    private toast: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.onboardingId = Number(
      this.route.snapshot.paramMap.get('id') ||
      this.route.parent?.snapshot.paramMap.get('oid')
    );

    this.seqSub = this.creditService.crdtPrpslSeq$.subscribe((seq) => {
      this.crdtPrpslSeq = seq;
      this.loadParagraphSection();
    });

    this.saveSub = this.creditService.saveSectionTrigger$.subscribe((sectionId) => {
      const currentRefValSeq = this.getRefValBySection(sectionId);

      if (currentRefValSeq && currentRefValSeq === this.refValSeq) {
        this.saveParagraphSection();
      }
    });

    this.initialized = true;
  }

  // ✅ jab Angular wahi instance reuse kare aur sirf inputs badlein
  ngOnChanges(changes: SimpleChanges): void {

    if (!this.initialized) {
      return; // ngOnInit already handle karega first load
    }

    if (changes['sectionId'] || changes['refValSeq']) {
      this.loadParagraphSection();
    }
  }

  ngOnDestroy(): void {
    this.saveSub?.unsubscribe();
    this.seqSub?.unsubscribe();
  }

  showDeletePopup = false;
  deleteMessage = '';
  private pendingDeleteIndex: number | null = null;

  get activeParagraphs(): ParagraphItem[] {
    return this.paragraphs.filter(p => p.delFlg !== 1);
  }

  // Shown in saved (ascending) order — new paragraphs are appended at the
  // bottom, and `number` matches the save payload's sortOrdr and the report.
  get displayParagraphs(): { item: ParagraphItem; number: number; index: number }[] {
    return this.activeParagraphs.map((item, index) => ({ item, number: index + 1, index }));
  }

  /** Drag-and-drop reorder; the new order is persisted via sortOrdr on save. */
  onParagraphDrop(event: CdkDragDrop<unknown>): void {
    this.paragraphs = moveVisibleItem(
      this.paragraphs,
      (p) => p.delFlg !== 1,
      event.previousIndex,
      event.currentIndex,
    );
  }

  addParagraph(): void {
    this.paragraphs.push({
      content: '',
      expanded: true,
      crdtPrpslBlkSeq: null,
      delFlg: 0,
    });
  }

  requestRemoveParagraph(index: number): void {
    this.pendingDeleteIndex = index;
    this.deleteMessage = 'CREDIT_SECTION_PARAGRAPH.DELETE_CONFIRM';
    this.showDeletePopup = true;
  }

  confirmRemoveParagraph(): void {
    if (this.pendingDeleteIndex === null) return;

    this.removeParagraph(this.pendingDeleteIndex);
    this.pendingDeleteIndex = null;
    this.toast.success('CREDIT_SECTION_PARAGRAPH.DELETED');
  }

  private removeParagraph(index: number): void {
    const selectedItem = this.activeParagraphs[index];

    if (!selectedItem) return;

    if (selectedItem.crdtPrpslBlkSeq) {
      selectedItem.delFlg = 1;
      selectedItem.expanded = false;
    } else {
      this.paragraphs = this.paragraphs.filter(p => p !== selectedItem);
    }
  }

  loadParagraphSection(): void {
    if (!this.crdtPrpslSeq) {
      this.paragraphs = [];
      return;
    }

    const setSeq = this.sectionId === 'company-introduction';

    this.creditService.getCrdtBlck(this.crdtPrpslSeq, [this.refValSeq]).subscribe({
      next: (res: any[]) => {
        this.handleLoadResponse(res, setSeq);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  private handleLoadResponse(res: any[], setSeq: boolean): void {

    if (!res?.length) {
      this.paragraphs = [];
      return;
    }

    if (setSeq) {
      this.creditService.setCrdtPrpslSeq(res[0]?.crdtPrpslSeq);
    }

    this.paragraphs = bySortOrder(res).map((item: any) => ({
      content: readParagraphBlockText(item),
      expanded: false,
      crdtPrpslBlkSeq: item.crdtPrpslBlkSeq ?? null,
      delFlg: item.delFlg ?? 0,
    }));
  }

  saveParagraphSection(): void {
    // A brand-new, never-saved paragraph (no crdtPrpslBlkSeq) left empty
    // can't just be soft-deleted — it has nothing to delete — so it blocks
    // the save. An existing paragraph emptied out by the user is instead
    // auto-marked delFlg 1 below, since there's a saved row to remove.
    const hasEmptyNewParagraph = this.paragraphs.some(
      (p) => (p.delFlg ?? 0) !== 1 && !hasRichTextContent(p.content) && !p.crdtPrpslBlkSeq,
    );

    if (hasEmptyNewParagraph) {
      const message = this.translate.instant('CREDIT_SECTION_PARAGRAPH.EMPTY_NOT_ALLOWED');

      this.toast.error(message);

      this.creditService.reportSectionSaveResult({
        sectionId: this.sectionId,
        status: 'validation-error',
        message,
      });

      return;
    }

    const payload = {
      crprtOnbrdngSeq: this.onboardingId,
      crdtPrpslSeq: this.crdtPrpslSeq,

      blockList: this.paragraphs.map((p, index) => {
        const emptiedExisting =
          !!p.crdtPrpslBlkSeq && (p.delFlg ?? 0) !== 1 && !hasRichTextContent(p.content);

        return {
          crdtPrpslBlkSeq: p.crdtPrpslBlkSeq ?? null,
          refGrp: this.refGrp,
          refValSeq: this.refValSeq,
          blkTyp: 1,
          delFlg: emptiedExisting ? 1 : (p.delFlg ?? 0),
          sortOrdr: index + 1,
          ...paragraphBlockData(p.content),
        };
      }),
    };

    this.creditService.saveSection(payload).subscribe({
      next: (res: any) => {
        this.creditService.setCrdtPrpslSeq(res?.crdtPrpslSeq);
        this.loadParagraphSection();

        this.creditService.reportSectionSaveResult({
          sectionId: this.sectionId,
          status: 'saved',
        });

        this.toast.success('CREDIT_SECTION.SECTION_SAVED');
      },
      error: (err) => {
        console.error(err);

        this.creditService.reportSectionSaveResult({
          sectionId: this.sectionId,
          status: 'api-error',
          message: err?.error?.message ?? this.translate.instant('CREDIT_SECTION.SECTION_SAVE_FAILED'),
        });
      },
    });
  }

  private getRefValBySection(sectionId: string): number | null {
    const mapping: Record<string, number> = {
      'company-introduction': 2519,
      'financial-analysis-paragraph': 2521,
      'borrower-request': 2524,
      'microfinance-plus-update': 2529,
      introduction: 2525,
    };

    return mapping[sectionId] ?? null;
  }
}